import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'

import { ALBUMS, SHOWCASES, SIM_SEARCH } from '@disco/constants/FeatureTypes'
import type { SearchType } from '@disco/constants/search'
import {
  getSearchResults,
  updateSearchParams,
} from '@disco/state-management/actions/SearchActions'
import { makeIsFeatureEnabled } from '@disco/state-management/selectors/business'
import { getDiscoTrackUrl } from '@disco/utils/getTrackSearchURL'
import { getYouTubeId } from '@disco/utils/getVideoEmbedURL'
import { getFiltersWithValidValues } from '@disco/utils/search'

import type { RootState } from 'store/configureStore'

import ViewSelector from '../view-selector'
import type { Option } from '../select-options'
import { searchTypeOptions } from '../select-options'

const mapStateToProps = (state: RootState) => {
  const isFeatureEnabled = makeIsFeatureEnabled()
  const simSearchEnabled = isFeatureEnabled(state, { featureName: SIM_SEARCH })
  const searchTerm = state.search.searchTerm
  // Detected client-side from the search term itself (not from
  // searchQuery.simSearch, which only lands in the store once the request
  // *succeeds* — if it fails or is still in flight, the type filter needs
  // to already know this is a similarity search).
  const isSimSearch = Boolean(
    simSearchEnabled &&
      searchTerm &&
      (getYouTubeId(searchTerm) || getDiscoTrackUrl(searchTerm)),
  )
  return {
    searchType: state.search.type,
    filters: state.search.filters,
    groups: state.search.groups,
    tags: state.search.tags,
    nativeTags: state.search.nativeTags,
    startDate: state.search.startDate,
    duration: state.search.duration,
    isSimSearch,
    albumsEnabled: isFeatureEnabled(state, { featureName: ALBUMS }),
    showcasesEnabled: isFeatureEnabled(state, { featureName: SHOWCASES }),
  }
}

const connector = connect(mapStateToProps, {
  updateSearchParams,
  getSearchResults,
})

type Props = ConnectedProps<typeof connector>

const SearchTypeFilter = ({
  searchType,
  filters,
  groups,
  tags,
  nativeTags,
  startDate,
  duration,
  isSimSearch,
  albumsEnabled,
  showcasesEnabled,
  updateSearchParams,
  getSearchResults,
}: Props): React.ReactElement => {
  // Metadata filters, track tags and duration only ever apply to tracks, so
  // All/Playlists/Albums must be blocked whenever any of them is set.
  const disableNonTrackOptions = Boolean(
    getFiltersWithValidValues(filters).length > 0 ||
      nativeTags.length ||
      duration !== null,
  )
  const disableArtistOption = Boolean(
    disableNonTrackOptions ||
      groups.length ||
      tags.length ||
      startDate.getTime(),
  )
  const options: Option[] = searchTypeOptions
    .filter(option => {
      if (option.value === 'artist') return showcasesEnabled
      if (option.value === 'album') return albumsEnabled
      return true
    })
    .map(option => {
      if (isSimSearch) {
        return option.value === 'track'
          ? option
          : {
              ...option,
              disabled: true,
              tooltip: 'Similarity Search only returns tracks',
            }
      }

      switch (option.value) {
        case '':
        case 'playlist':
        case 'album':
          return {
            ...option,
            disabled: disableNonTrackOptions,
            tooltip: disableNonTrackOptions
              ? `To use the ${
                  option.value || 'all'
                } filter, clear the metadata filter, track tags, or duration filter.`
              : undefined,
          }
        case 'artist':
          return {
            ...option,
            disabled: disableArtistOption,
            tooltip: disableArtistOption
              ? 'To use the artist filter, clear the other search terms.'
              : undefined,
          }
        default:
          return option
      }
    })

  return (
    <ViewSelector
      value={searchType || ''}
      options={options}
      onChange={(type: SearchType | '') => {
        updateSearchParams({ type: type || false })
        getSearchResults()
      }}
    />
  )
}

export default connector(SearchTypeFilter)
